// Fila de Próximos Contatos (V4, VV) — regras puras, fora da página para testar
// sem React.

/**
 * Etapa ATUAL do lead de uma tarefa.
 *
 * `tarefa.leadEtapa` é desnormalizado na criação e nunca atualizado — a pílula
 * mostrava a etapa de quando a tarefa nasceu. A página vive dentro do
 * `LeadsProvider`, então o lead está em memória: a etapa viva vem dele. O campo
 * congelado fica só como reserva para lead que não está na lista (apagado).
 */
export const etapaDaTarefa = (tarefa, leads = []) =>
  leads.find((l) => l.id === tarefa?.leadId)?.etapa ?? tarefa?.leadEtapa ?? '';

/**
 * ★ VV (decisão do dono, 20/set/2026) — a página tem DUAS seções:
 *
 *   Agendamentos     quem NÃO AGENDOU ou NÃO COMPARECEU — trabalho da SDR
 *   Follow-up        quem COMPARECEU e NÃO FECHOU — trabalho do negociador
 *
 * ★★ PC-1 (decisão do dono, 17/out/2026) — acabou a seção "Outros contatos":
 * *"Quem está agendado ou novo vai para agendamento. E quem fechou vai para follow
 * up."* ⚠ Medido em 17/out: os 38 "outros" de Campinas eram 24 agendado, 12
 * fechado e 2 novo — e nenhum de etapa antiga ou vazia (`docs/pc1-testes.md`).
 * ★ Etapa que não está em lista nenhuma (antiga, vazia, tarefa sem lead) cai em
 * Agendamentos: tarefa aberta nunca fica invisível.
 *
 * A seção é DERIVADA da etapa ATUAL do lead (`etapaDaTarefa`): a tarefa muda de
 * seção quando o lead se move, de propósito — se a pessoa reagendou, o contato
 * pendente deixou de ser "de agendamento". O carimbo `leadEtapa` só decide quando
 * o lead não está em memória. Nenhum campo novo, nenhuma consulta nova: a
 * divisão é no cliente, como o funil.
 *
 * `funcao` é a função (VP) cujo trabalho a seção representa — usada SÓ para a
 * abertura padrão da tela (`secoesAbertasPorFuncao`), nunca para acesso.
 */
export const SECOES = [
  {
    id: 'agendamentos',
    label: 'Agendamentos',
    etapas: ['novo', 'nao_agendou', 'agendado', 'nao_compareceu'],
    funcao: 'sdr',
    vazio: 'Leads em Novo, Não agendou, Agendado ou Não compareceu com data de retorno aparecem aqui.',
  },
  {
    id: 'followup',
    label: 'Follow-up',
    etapas: ['compareceu', 'nao_fechou', 'fechado'],
    funcao: 'negociador',
    vazio: 'Leads em Compareceu, Não fechou ou Fechado com data de retorno aparecem aqui.',
  },
];

/** ★ PC-1 — para onde vai a etapa que não está em lista nenhuma. */
const SECAO_RESERVA = 'agendamentos';

/**
 * Em qual seção a tarefa aparece. Sempre devolve uma das duas: tarefa aberta
 * nunca fica invisível (as 12 importadas sem responsável ensinaram isso).
 * Tarefa sem `leadId` decide pelo carimbo, se houver; sem nada → Agendamentos.
 */
export function secaoDaTarefa(tarefa, leads = []) {
  const etapa = etapaDaTarefa(tarefa, leads);
  return SECOES.find((s) => s.etapas.includes(etapa))?.id ?? SECAO_RESERVA;
}

/**
 * Quais seções começam ABERTAS, pela função de quem olha — preferência de TELA,
 * não permissão: todas as seções são renderizadas para quem tem acesso à página.
 * Só negociador → Follow-up; só SDR → Agendamentos; as duas, nenhuma ou
 * desconhecido → ambas.
 */
export function secoesAbertasPorFuncao(funcoes = []) {
  const lista = Array.isArray(funcoes) ? funcoes : [];
  const abertas = new Set();
  const soUma = lista.length === 1 && SECOES.some((s) => s.funcao === lista[0]);
  for (const s of SECOES) {
    if (!soUma || s.funcao === lista[0]) abertas.add(s.id);
  }
  return abertas;
}

const VAZIO = Object.freeze({ id: '', nome: '', herdado: false });

/**
 * ★ VV-c1 (decisão do dono, 20/set/2026) — de quem é a tarefa, na LEITURA.
 *
 * `responsavelId` é carimbado na criação e nunca mais acompanha o lead: o dono
 * trocou o SDR na ficha e a tarefa continuou "sem responsável"; as 12 importadas
 * nasceram sem dono e ficaram órfãs. Em vez de reescrever tarefas, a fila
 * DERIVA: o gravado manda; vazio → o SDR ATUAL do lead em memória (`herdado`);
 * lead sem SDR, lead fora da memória ou tarefa sem `leadId` → vazio, sem
 * inventar. Mesmo espírito da seção (`secaoDaTarefa`): lê o lead vivo, não o
 * carimbo. NADA escreve `responsavelId` por causa disto.
 */
export function responsavelDaTarefa(tarefa, leads = []) {
  if (!tarefa) return VAZIO;
  if (tarefa.responsavelId) {
    return { id: tarefa.responsavelId, nome: tarefa.responsavelNome ?? '', herdado: false };
  }
  const lead = tarefa.leadId ? leads.find((l) => l.id === tarefa.leadId) : null;
  if (!lead?.sdrId) return VAZIO;
  return { id: lead.sdrId, nome: lead.sdrNome ?? '', herdado: true };
}

/**
 * Filtro "Responsável" no cliente. Com uma pessoa escolhida, entram as tarefas
 * dela — gravadas OU herdadas do SDR do lead (VV-c1) — E as SEM responsável de
 * verdade (decisão do dono): ninguém deve precisar trocar de filtro para achar
 * trabalho órfão. Sem filtro, tudo entra.
 */
export const tarefaDoResponsavel = (tarefa, responsavelId, leads = []) => {
  if (!responsavelId) return true;
  const dono = responsavelDaTarefa(tarefa, leads).id;
  return !dono || dono === responsavelId;
};
